import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ProtectedRoute } from "@/lib/protected-route";
import { Redirect } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getQueryFn } from "@/lib/queryClient";
import { SmilePlus, Smile, Meh, Frown, Frown as FrownSad, Loader2 } from "lucide-react";

// Interface for feedback data
interface Feedback {
  id: number;
  csatRating: number;
  comment: string | null;
  contactNumber: string | null;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  page: string | null;
  feedbackType: string | null;
}

const COLORS = ["#ff6b6b", "#ff9f43", "#feca57", "#48dbfb", "#1dd1a1"];

// Get the emoji for a CSAT rating
const getRatingEmoji = (rating: number) => {
  switch (rating) {
    case 1: return <FrownSad className="h-5 w-5 text-red-500" />;
    case 2: return <Frown className="h-5 w-5 text-orange-500" />;
    case 3: return <Meh className="h-5 w-5 text-yellow-500" />;
    case 4: return <Smile className="h-5 w-5 text-blue-400" />;
    case 5: return <SmilePlus className="h-5 w-5 text-green-500" />;
    default: return null;
  }
};

// Format date to display nicely
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Dashboard component for displaying feedback data
const FeedbackDashboard = () => {
  const { t } = useTranslation();
  const { user, isLoading: authLoading, logoutMutation } = useAuth();
  const { toast } = useToast();
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  
  // Update screen width state on resize
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // API response type for our new paginated endpoint
  interface FeedbackApiResponse {
    stats: {
      totalCount: number;
      avgRating: number;
      commentCount: number;
    };
    ratingStats: {
      rating: number;
      count: number;
    }[];
    data: Feedback[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }

  const {
    data: apiResponse,
    isLoading,
    error,
  } = useQuery<FeedbackApiResponse>({
    queryKey: ["/api/feedback", { page, limit: pageSize }],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!user?.isAdmin,
    refetchInterval: 60000, // Reduce refetch frequency to once per minute
  });
  
  // Extract data from response
  const feedbacks = apiResponse?.data || [];
  const stats = apiResponse?.stats;
  const ratingStats = apiResponse?.ratingStats || [];
  const pagination = apiResponse?.pagination;

  // Return early if user is not authorized
  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!user?.isAdmin) {
    toast({
      title: t("Unauthorized"),
      description: t("You don't have permission to access this page."),
      variant: "destructive",
    });
    return <Redirect to="/" />;
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-destructive mb-2">
            {t("Error Loading Feedback")}
          </h2>
          <p className="text-muted-foreground">
            {(error as Error).message || t("Failed to load feedback data.")}
          </p>
        </div>
      </div>
    );
  }

  // Prepare data for charts from server-provided statistics
  const ratingData = [
    { name: t("Very Dissatisfied"), value: 0, rating: 1 },
    { name: t("Dissatisfied"), value: 0, rating: 2 },
    { name: t("Neutral"), value: 0, rating: 3 },
    { name: t("Satisfied"), value: 0, rating: 4 },
    { name: t("Very Satisfied"), value: 0, rating: 5 },
  ];
  
  // Map the server-provided rating stats to our format
  if (ratingStats?.length) {
    ratingStats.forEach(stat => {
      const index = stat.rating - 1;
      if (index >= 0 && index < ratingData.length) {
        ratingData[index].value = stat.count;
      }
    });
  }
  
  // Get dashboard stats from API
  const totalRatings = stats?.totalCount || 0;
  const averageRating = stats?.avgRating ? Number(stats.avgRating).toFixed(1) : "N/A";
  const commentCount = stats?.commentCount || 0;

  return (
    <div className="container mx-auto py-10 space-y-8 overflow-y-auto h-screen px-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold">{t("Feedback Dashboard")}</h1>
        <Button 
          variant="outline" 
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
          className="flex items-center gap-2"
        >
          {logoutMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          )}
          {t("Logout")}
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("Total Feedbacks")}</CardTitle>
            <CardDescription>{t("All-time submissions")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{totalRatings}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>{t("Average Rating")}</CardTitle>
            <CardDescription>{t("Out of 5 stars")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <p className="text-4xl font-bold">{averageRating}</p>
              <SmilePlus className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>{t("Feedback with Comments")}</CardTitle>
            <CardDescription>{t("Detailed feedback")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">
              {commentCount}
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("Rating Distribution")}</CardTitle>
            <CardDescription>{t("Breakdown of user satisfaction")}</CardDescription>
          </CardHeader>
          <CardContent className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ratingData}
                margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rating" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name={t("Responses")}>
                  {ratingData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>{t("Rating Percentage")}</CardTitle>
            <CardDescription>{t("Proportion of each rating")}</CardDescription>
          </CardHeader>
          <CardContent className="h-60 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={ratingData.filter(d => d.value > 0)}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={screenWidth < 768 ? 60 : 80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => 
                    screenWidth < 400 
                      ? `${(percent * 100).toFixed(0)}%` 
                      : `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                >
                  {ratingData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>{t("All Feedback")}</CardTitle>
          <CardDescription>{t("Detailed view of user submissions")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">{t("ID")}</TableHead>
                  <TableHead className="w-[80px]">{t("Rating")}</TableHead>
                  <TableHead>{t("Comment")}</TableHead>
                  <TableHead className="w-[100px]">{t("Type")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("Contact")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("Date")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedbacks?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10">
                      {t("No feedback data available")}
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks?.map((feedback) => (
                    <TableRow key={feedback.id}>
                      <TableCell className="font-medium">{feedback.id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {feedback.csatRating}
                          {getRatingEmoji(feedback.csatRating)}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[150px] md:max-w-[250px] lg:max-w-lg truncate">
                        {feedback.comment || t("No comment")}
                      </TableCell>
                      <TableCell>
                        {feedback.feedbackType || t("General")}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {feedback.contactNumber || t("Not provided")}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{formatDate(feedback.createdAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                disabled={page === 1}
              >
                {t("Previous")}
              </Button>
              <div className="text-sm text-muted-foreground">
                {t("Page {{current}} of {{total}}", { current: page, total: pagination.totalPages })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(prev => Math.min(prev + 1, pagination.totalPages))}
                disabled={page === pagination.totalPages}
              >
                {t("Next")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Wrap the component with the ProtectedRoute for authentication
const ProtectedFeedbackDashboard = () => (
  <ProtectedRoute
    path="/admin/feedback"
    component={FeedbackDashboard}
  />
);

export default ProtectedFeedbackDashboard;